// React
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";

// User Actions
import { getYearByIdAction } from "../../actions/yearActions";
import { listDays } from "../../actions/dayActions";
import {
  createWeekAction,
  getWeekbyIdAction,
  patchWeekAction,
} from "../../actions/weekActions";

// Multi-device sync — mirrors DayScreen / YearScreen integration.
import {
  markDirty,
  flushNow,
  registerSender,
  installUnloadHooks,
  discardFields,
  getOrCreateDeviceId,
} from "../../utils/syncQueue";
import {
  subscribe as socketSubscribe,
  unsubscribe as socketUnsubscribe,
  onPatch as socketOnPatch,
  onReconnect as socketOnReconnect,
  getSocketId,
} from "../../services/socket";
import { WEEKS_PATCH_SUCCESS } from "../../constants/weekConstants";

// Internal Components
import Header from "../../components/layouts/Header/Header";
import PageContainer from "../../components/layouts/Containers/PageContainer";
import WeekDock from "./components/WeekDock";
import Board from "../../features/objectives/Board";
import TestFooter from "../../components/layouts/Footer/TestFooter";
import FloatingInfoPanel from "../../components/ui/FloatingInfoPanel/FloatingInfoPanel";
import { LoadingTile } from "../../components/ui/Loading/LoadingTile";
import SaveStatusIndicator from "../../components/ui/SaveStatus/SaveStatusIndicator";

// Custom Hooks
import useResponsiveState from "../../hooks/useResponsiveState";
import { usePersistentLoading } from "../../hooks/usePersistentLoading";
import { useLoadingContent } from "../../hooks/useLoadingContent";
import { useLoadingFloor } from "../../hooks/useLoadingFloor";
import useObjectivesState from "../../features/objectives/hooks/useObjectivesState";

// Icons
import {
  OneIconMob,
  TwoIconMob,
  ThreeIconMob,
  FourIconMob,
  FiveIconMob,
  SixIconMob,
  SevenIconMob,
  EightIconMob,
  NineIconMob,
  TenIconMob,
} from "../../components/ui/Icons/Icon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { labelMaker } from "../../utils/UtilityFunctions";

// Utils
import axios from "axios";

// Styling
import "./weeksscreen.css";
import SEO from "../../components/seo/SEO";

function WeekScreen({ history }) {
  // Hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { viewType, data } = useResponsiveState();
  const { id } = useParams();

  const loadingContent = useLoadingContent();
  const pageLoading = useLoadingFloor(10000, loadingContent);
  const { isShowing, startLoading, stopLoading, tileProps } =
    usePersistentLoading(pageLoading, loadingContent);

  // State
  // Redux
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;

  const weekCreate = useSelector((state) => state.weekCreate);
  const { error, weeks } = weekCreate;

  const yearList = useSelector((state) => state.yearList);
  const yearItem = yearList.years?.at(-1);

  const yearSingle = useSelector((state) => state.yearSingle);

  const weekSingle = useSelector((state) => state.weekSingle?.week);

  const dayList = useSelector((state) => state.dayList); //
  const { days, loading } = dayList;

  const isCurrentlyFetching = loading;

  useEffect(() => {
    // This hook now directly reflects the Redux state.
    if (isCurrentlyFetching) {
      startLoading();
    } else {
      stopLoading();
    }
  }, [isCurrentlyFetching, startLoading, stopLoading]);

  // Initialised
  useEffect(() => {
    if (id) {
      dispatch(getWeekbyIdAction(id));
    }
  }, [userInfo, history]);

  useEffect(() => {
    if (yearList && yearList.years && yearItem)
      dispatch(getYearByIdAction(yearItem._id));
  }, [yearList, yearItem, userInfo]);

  useEffect(() => {
    dispatch(listDays());
  }, [dispatch, userInfo, history]);

  const [pageState, setPageState] = useState(
    id ? "Update Week" : "Create Week",
  );
  const [cursorState, setCursorState] = useState("false");
  const [fontState, setFontState] = useState(72);
  const [cursorDisplayState, setCursorDisplayState] = useState(false);
  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [calloutActive, setCalloutActive] = useState(false);
  const [mouseState, setMouseState] = useState("");
  const [viewWidth, setViewWidth] = useState(
    Math.max(document.documentElement.clientWidth),
  );
  const {
    objectives,
    columnOrder,
    setObjectives,
    setColumnOrder,
    changeObjectiveTitle,
    deleteObjective,
    reorderObjectives,
    handleSort,
    onDragEnd,
    addSubTask,
    toggleCompletedSubTasks,
    handleEstimateChange,
    changeSubTaskUrgency,
    updateDescription,
    updateNotes,
    deleteSubtask,
    addStep,
    toggleStep,
    updateStep,
    deleteStep,
    onDragEndStep,
  } = useObjectivesState();
  const [meyear, setMeyear] = useState({});
  const [load, setLoad] = useState("");

  const [displayState, setDisplayState] = useState({
    "obj-one": true,
    "obj-two": false,
    "obj-three": false,
    "obj-four": false,
    "obj-five": false,
    "obj-six": false,
    "obj-seven": false,
    "obj-eight": false,
    "obj-nine": false,
    "obj-ten": false,
  });

  const numIconObj = {
    "obj-one": OneIconMob,
    "obj-two": TwoIconMob,
    "obj-three": ThreeIconMob,
    "obj-four": FourIconMob,
    "obj-five": FiveIconMob,
    "obj-six": SixIconMob,
    "obj-seven": SevenIconMob,
    "obj-eight": EightIconMob,
    "obj-nine": NineIconMob,
    "obj-ten": TenIconMob,
  };

  const ALL_OBJECTIVE_KEYS = [
    "obj-one",
    "obj-two",
    "obj-three",
    "obj-four",
    "obj-five",
    "obj-six",
    "obj-seven",
    "obj-eight",
    "obj-nine",
    "obj-ten",
  ];

  const activeObjectiveKeys = useMemo(() => {
    if (!objectives) return [];
    return ALL_OBJECTIVE_KEYS.filter(
      (key) => objectives[key] && objectives[key].title.trim() !== "",
    );
  }, [objectives]);

  const firstEmptyKey = useMemo(() => {
    if (!objectives) return null;
    return (
      ALL_OBJECTIVE_KEYS.find(
        (key) => !objectives[key] || objectives[key].title.trim() === "",
      ) || null
    );
  }, [objectives]);

  const currentActiveKey = useMemo(() => {
    return Object.keys(displayState).find((key) => displayState[key]);
  }, [displayState]);

  const navigateToObjective = useCallback((key) => {
    setDisplayState((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => {
        next[k] = k === key;
      });
      return next;
    });
  }, []);

  // Remote patches awaiting user opt-in. Week's kanban state is fragile
  // to silent remote updates mid-drag, so we always surface them via the
  // banner — the user clicks Apply to absorb.
  const [incomingPending, setIncomingPending] = useState({});

  // Cancel any prior 2s clear-timer before scheduling a new one so
  // rapid back-to-back saves don't leave orphaned timeouts that
  // flicker the dock icon between green and default. See DayScreen.js
  // for the matching pattern.
  const saveIconTimerRef = useRef(null);
  const handleSave = useCallback(() => {
    setLoad("success");
    if (saveIconTimerRef.current) clearTimeout(saveIconTimerRef.current);
    saveIconTimerRef.current = setTimeout(() => {
      setLoad("");
      saveIconTimerRef.current = null;
    }, 2000);
  }, []);
  useEffect(
    () => () => {
      if (saveIconTimerRef.current) clearTimeout(saveIconTimerRef.current);
    },
    [],
  );

  const handleRefresh = async () => {
    setLoad("success");
    if (id) {
      await dispatch(getWeekbyIdAction(id));
    }
    setLoad("");
  };

  useEffect(() => {
    if (id) {
      setPageState("Update | Week");
    }
  }, [id]);

  // Guards the autosave effect further below: set to true whenever
  // objectives/columnOrder change for a reason OTHER than a local edit
  // (initial load, server sync, accepting a remote patch) so that change
  // doesn't get immediately markDirty'd and PATCHed straight back.
  const skipNextAutosaveRef = useRef(true);

  useEffect(() => {
    if (!weekSingle) {
      setColumnOrder(Object.keys(objectives));
    } else {
      setObjectives(weekSingle.objectives);
      setColumnOrder(weekSingle.columnOrder);
    }
    skipNextAutosaveRef.current = true;
  }, [id, weekSingle]);

  useEffect(() => {
    if (yearSingle && yearSingle.year) {
      setMeyear((meyear) => {
        if (yearSingle.year.values) {
          return {
            obj: yearSingle.year,
            values_arr: Object.keys(yearSingle.year.values),
          };
        } else {
          return {
            obj: yearSingle.year,
            values_arr: [],
          };
        }
      });
    }
  }, [yearSingle]);

  const [aiLoading, setAiLoading] = useState(false);

  const fetchOpenAIResponse = async (prompt) => {
    try {
      const response = await axios.post(
        "https://api.nowasteddays.io/planning",
        {
          message: prompt,
        },
      );

      if (response) {
        return response.data.output[0].content[0].text;
      }
    } catch (error) {
      console.error("Error fetching response:", error);
      return "Error: Unable to fetch response.";
    }
  };

  const handleAiStepsAddition = async (task, steps) => {
    // Make the function async
    // aiAddSteps
    // params
    // - task
    // role of function
    // - Add steps to existing steps array
    // process
    // - Validate description of task (validation logic not shown, add if needed)
    // - kick off AI with task param
    // - wait for response
    // - setSteps with response (or update existing steps)

    try {
      const newSteps = await fetchOpenAIResponse(task); // Await the promise

      if (Array.isArray(newSteps)) {
        // If you are using a state setter like setSteps from useState:
        // setSteps(prevSteps => [...prevSteps, ...newSteps]);

        // If 'steps' is a mutable array passed by reference and you want to modify it directly:
        // (This is generally not recommended for React state, prefer immutable updates)
        for (let i = 0; i < newSteps.length; i++) {
          steps.push(newSteps[i]);
        }
        // If 'steps' is React state and 'setSteps' is its updater:
        // setSteps(currentSteps => [...currentSteps, ...newSteps]);
      } else {
        console.error("AI response is not an array:", newSteps);
      }
    } catch (error) {
      console.error("Error fetching AI steps:", error);
      // Optionally, handle the error in the UI, e.g., set an error state
    }
  };

  const handleColumnChange = (e) => {
    let data = e.currentTarget.dataset;
    let newColumn = data.name;

    setDisplayState((displayState) => {
      let shallow = { ...displayState };

      Object.keys(shallow).map((el) => {
        if (el === newColumn) {
          shallow[el] = true;
        } else {
          shallow[el] = false;
        }
      });

      return shallow;
    });
  };

  const handleOverlayChange = (newState) => {
    setOverlayVisible(newState);
  };

  const handleCalloutDisplay = () => {
    setCalloutActive((calloutActive) => !calloutActive);
  };

  // Register the week sender once. Individual saves are queued below
  // whenever objectives/columnOrder change; the manual Save button and the
  // nav "nwd:save" event just force-flush that queue early.
  useEffect(() => {
    registerSender("week", async (recordId, payload) => {
      try {
        const enriched = { ...payload, socketId: getSocketId() };
        const response = await dispatch(patchWeekAction(recordId, enriched));
        handleSave();
        return response;
      } catch (err) {
        setLoad("error");
        throw err;
      }
    });
    installUnloadHooks(process.env.REACT_APP_API_URL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Autosave — mirrors DayScreen/YearScreen's per-field markDirty pattern.
  // Week's board state is a single objectives/columnOrder blob updated by
  // many handlers in useObjectivesState, so instead of wiring markDirty
  // into each one individually we mark both fields dirty whenever the
  // blob changes. markDirty schedules its own 500ms debounced flush, so
  // this is what makes the week save without the (desktop-only) button.
  useEffect(() => {
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }
    if (!id) return;
    markDirty("week", id, "objectives", objectives);
    markDirty("week", id, "columnOrder", columnOrder);
  }, [objectives, columnOrder, id]);

  // Flush any pending edits when the user navigates away from this week.
  useEffect(() => {
    if (!id) return undefined;
    return () => {
      flushNow("week", id).catch(() => {});
    };
  }, [id]);

  // Subscribe to live broadcasts for the current week. Unlike Day/Year,
  // Week never silently overwrites local state — the kanban drag-state is
  // too fragile. Every incoming patch lands in incomingPending and the
  // user must click Apply.
  useEffect(() => {
    if (!id) return undefined;
    socketSubscribe("week", id);

    const offPatch = socketOnPatch((msg) => {
      if (!msg || msg.type !== "week" || msg.id !== id) return;
      if (msg.originDeviceId && msg.originDeviceId === getOrCreateDeviceId())
        return;
      const incoming = msg.patches || {};
      if (Object.keys(incoming).length > 0) {
        setIncomingPending((prev) => ({ ...prev, ...incoming }));
      }
      if (typeof msg.version === "number" || msg.fieldMeta) {
        dispatch({
          type: WEEKS_PATCH_SUCCESS,
          payload: { version: msg.version, fieldMeta: msg.fieldMeta },
        });
      }
    });

    const offReconnect = socketOnReconnect(() => {
      dispatch(getWeekbyIdAction(id));
    });

    return () => {
      offPatch();
      offReconnect();
      socketUnsubscribe("week", id);
    };
  }, [id, dispatch]);

  const acceptIncomingChanges = () => {
    if (Object.keys(incomingPending).length === 0) return;
    // This update is absorbing a remote change, not a local edit — skip
    // the next autosave run so we don't immediately PATCH the same data
    // straight back to the server.
    skipNextAutosaveRef.current = true;
    if ("objectives" in incomingPending) {
      setObjectives(incomingPending.objectives);
    }
    if ("columnOrder" in incomingPending) {
      setColumnOrder(incomingPending.columnOrder);
    }
    discardFields("week", id, Object.keys(incomingPending));
    setIncomingPending({});
  };

  const handleSubmitRef = useRef(null);
  useEffect(() => {
    const onNavSave = () => handleSubmitRef.current?.();
    window.addEventListener("nwd:save", onNavSave);
    return () => window.removeEventListener("nwd:save", onNavSave);
  }, []);

  const handleSubmit = async () => {
    if (id === undefined) {
      await dispatch(createWeekAction(objectives, columnOrder))
        .then(async (response) => {
          if (response) {
            let nextPage = await response._id;
            navigate(`/week/${nextPage}`);
          } else {
            console.log("Error");
          }
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      // Push both fields through the sync queue and flush. The server
      // applies field-level LWW + broadcasts to other devices.
      markDirty("week", id, "objectives", objectives);
      markDirty("week", id, "columnOrder", columnOrder);
      flushNow("week", id).catch((error) => {
        console.log(error);
      });
    }
  };
  handleSubmitRef.current = handleSubmit;

  const handleMouseState = (str) => {
    if (str === mouseState) {
      setCursorDisplayState(false);
    } else {
      setMouseState(str);
      setCursorDisplayState(true);
    }
  };

  useEffect(() => {
    if (yearSingle && yearSingle.year) {
      setMeyear((meyear) => {
        if (yearSingle.year.values) {
          return {
            obj: yearSingle.year,
            values_arr: Object.keys(yearSingle.year.values),
          };
        } else {
          return {
            obj: yearSingle.year,
            values_arr: [],
          };
        }
      });
    }
  }, [yearSingle]);

  const closeTooltip = () => {
    setCursorDisplayState(false);
  };

  const hasIncoming = Object.keys(incomingPending).length > 0;

  return (
    <>
      <SaveStatusIndicator recordType="week" />
      {hasIncoming && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            top: 12,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            background: "rgba(0, 74, 35, 0.95)",
            color: "white",
            padding: "10px 16px",
            borderRadius: 8,
            boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
            fontSize: 14,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <span>Updated on another device</span>
          <button
            type="button"
            onClick={acceptIncomingChanges}
            style={{
              background: "white",
              color: "rgb(0, 74, 35)",
              border: "none",
              padding: "4px 10px",
              borderRadius: 4,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Apply
          </button>
        </div>
      )}
      <SEO
        title={pageState}
        description="Create and update your week planner."
        canonicalUrl={"https://nowasteddays.io/week/create"}
      />
      <Header variant="light" active={true} />
      <FloatingInfoPanel
        viewType={viewType}
        mouseState={mouseState}
        handleMouseState={handleMouseState}
        meyear={meyear}
        cursorDisplayState={cursorDisplayState}
        closeTooltip={closeTooltip}
      />
      <form>
        <PageContainer className={"wekContainer"}>
          {isShowing ? (
            <LoadingTile content={loadingContent} {...tileProps} />
          ) : (
            objectives && (
              <>
                <Board
                  initial={objectives}
                  ordered={columnOrder}
                  overlay={isOverlayVisible}
                  onDragEnd={onDragEnd}
                  onDragEndSteps={onDragEndStep}
                  handleUrgencyChange={changeSubTaskUrgency}
                  handleEstimateChange={handleEstimateChange}
                  handleSort={handleSort}
                  yearList={yearSingle}
                  display={displayState}
                  addSubTask={addSubTask}
                  addStep={addStep}
                  aiHelp={handleAiStepsAddition}
                  toggleSubTask={toggleCompletedSubTasks}
                  toggleStep={toggleStep}
                  toggleDisplay={handleCalloutDisplay}
                  toggleOverlay={handleOverlayChange}
                  updateTitle={changeObjectiveTitle}
                  updateOrder={reorderObjectives}
                  updateDescription={updateDescription}
                  updateStep={updateStep}
                  updateNotes={updateNotes}
                  deleteObjective={deleteObjective}
                  deleteStep={deleteStep}
                  deleteSubtask={deleteSubtask}
                  viewType={viewType}
                  withScrollableColumns
                />

                {viewType === "desktop" && (
                  <WeekDock
                    viewType={viewType}
                    dockLoading={pageLoading}
                    meyear={yearSingle.year}
                    yearid={yearItem && yearItem._id && yearItem._id}
                    yearList={yearList}
                    load={load}
                    handleSubmit={handleSubmit}
                    handleMouseState={handleMouseState}
                    handleRefresh={handleRefresh}
                  />
                )}
              </>
            )
          )}
        </PageContainer>
      </form>

      {viewType !== "desktop" && objectives && (
        <nav
          className="nwdnav__tabs nwdnav__tabs--week"
          aria-label="Objectives"
        >
          {activeObjectiveKeys.map((key) => {
            const IconComponent = numIconObj[key];
            return (
              <button
                key={key}
                className={`nwdnav__tab${
                  currentActiveKey === key ? " nwdnav__tab--active" : ""
                }`}
                onClick={() => navigateToObjective(key)}
              >
                <span className="nwdnav__tab-icon">
                  <IconComponent
                    opacity={objectives[key]?.score}
                    text={
                      objectives[key]?.title &&
                      labelMaker(objectives[key].title)
                    }
                  />
                </span>
                {/* <span className="nwdnav__tab-label">
                  {objectives[key]?.title
                    ? objectives[key].title.length > 8
                      ? objectives[key].title.slice(0, 8) + "..."
                      : objectives[key].title
                    : ""}
                </span> */}
              </button>
            );
          })}
          {firstEmptyKey && (
            <button
              className="nwdnav__tab"
              onClick={() => navigateToObjective(firstEmptyKey)}
            >
              <span className="nwdnav__tab-icon">
                <FontAwesomeIcon icon={faPlus} />
              </span>
              <span className="nwdnav__tab-label">Add</span>
            </button>
          )}
        </nav>
      )}

      <TestFooter />
    </>
  );
}

export default WeekScreen;
